import { useContext, useRef, useState } from "react";
import MainModal from "components/MainModal";
import { useTranslation } from "react-i18next";
import { useStyles } from "./RCAAddActionPlanModal.styles";
import IssueIcon from "pages/Corporate/RCA/images/issue_icon.svg";
import RCAIcon from "pages/Corporate/RCA/images/rca_icon.svg";
import ActionPlanIcon from "pages/Corporate/RCA/images/action_plan_icon.svg";
import { Formik, FormikProps, Form } from "formik";
import { useRCAMainStyles } from "../../RCA.styles";
import { addRCAActionRequestFormValidationSchema } from "validations/schemas";
import { IRCAAddActionPlanModal, IRCAAddActionPlanModalFormValues } from "./RCAAddActionPlanModalInterfaces";
import { Box, Button, Divider, FormHelperText, InputLabel, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import RCAPriorityDropdown from "../RCAPriorityDropdown";
import { RCA_PRIORITIES, RCA_STATUSES } from "constants/constants";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";
import DoneIcon from "@mui/icons-material/Done";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import RCAUsersDropdown from "../RCAUsersDropdown";
import { IActionPlan } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import useDateFormat from "hooks/useDateFormat";
import { UserContext } from "contexts/UserContext/UserContext";
import moment from "moment";
import RCAMeetingTypesDropdown from "../RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";

const RCAAddActionPlanModal = ({
    open,
    onClose,
    issue,
    rca,
    onAddActionPlan,
    isFromActionPlan,
    onAddActionPlanFromOtherActionPlan,
}: IRCAAddActionPlanModal) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { getDateFormatByLanguage } = useDateFormat();
    const formRef = useRef<FormikProps<IRCAAddActionPlanModalFormValues>>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { createActionPlan } = ActionPlanEntity.InsertActionPlan();
    const [addOtherActionPlan, setAddOtherActionPlan] = useState(false);
    const { user_id } = useContext(UserContext);

    const initFormValues: IRCAAddActionPlanModalFormValues = {
        actionPlanName: "",
        actionPlanDescription: "",
        statusId: RCA_STATUSES.NEEDS_APPROVAL,
        dueDate: moment(new Date()).add(4, "days").toDate(),
        assignedToId: "",
        priorityId: RCA_PRIORITIES.LOW,
        meetingTypeId: rca?.meeting_type_id || issue?.meeting_type_id || "",
    };

    const handleSubmit = async (values: IRCAAddActionPlanModalFormValues) => {
        setIsSaving(true);
        const actionPlanValues = {
            ...(issue && { issue_id: issue?.issue_id }),
            ...(rca && { rca_id: rca?.rca_id }),
            created_by: user_id,
            due_date: values.dueDate,
            submitted_by_user_id: user_id,
            assignee_id: values.assignedToId,
            description: values.actionPlanDescription,
            priority_id: values.priorityId,
            status_id: values.statusId,
            name: values.actionPlanName || t("rca.action_plans.add_action_plan_modal.new_action_plan"),
            ...(values.meetingTypeId && { meeting_type_id: values.meetingTypeId }),
        };

        try {
            const response = await createActionPlan({ variables: { action_plan: actionPlanValues } });

            setIsSaving(false);
            if (response && !addOtherActionPlan) {
                const newActionPlan: IActionPlan = response.data?.insert_action_plan_one;
                onAddActionPlan(newActionPlan);
            }
            if (response && addOtherActionPlan) {
                onAddActionPlanFromOtherActionPlan?.();
                formRef.current?.resetForm();
                formRef.current?.setValues(initFormValues);
                setAddOtherActionPlan(false);
            }
        } catch {
            setIsSaving(false);
            return;
        }
    };

    const footer = (
        <div className={isFromActionPlan ? classes.footerWithThirdButton : classes.footer}>
            <Button variant="text" color="info" onClick={onClose}>
                {t("main_modal.cancel_button")}
            </Button>
            {isFromActionPlan && (
                <div className={classes.footerMainActions}>
                    <Button
                        variant="outlined"
                        color="info"
                        onClick={() => {
                            setAddOtherActionPlan(true);
                            if (formRef.current) {
                                formRef.current.handleSubmit();
                            }
                        }}
                    >
                        <AddIcon />
                        {t("rca.action_plans.add_action_plan_modal.add_other")}
                    </Button>
                    <Button
                        variant="contained"
                        color="info"
                        onClick={() => {
                            if (formRef.current) {
                                formRef.current.handleSubmit();
                            }
                        }}
                    >
                        <DoneIcon />
                        {t("rca.action_plans.add_action_plan_modal.done")}
                    </Button>
                </div>
            )}
            {!isFromActionPlan && (
                <Button
                    variant="contained"
                    color="info"
                    onClick={() => {
                        if (formRef.current) {
                            formRef.current.handleSubmit();
                        }
                    }}
                >
                    <DoneIcon />
                    {t("rca.action_plans.add_action_plan_modal.done")}
                </Button>
            )}
        </div>
    );

    return (
        <MainModal
            isOpen={open}
            onClose={onClose}
            title={t("rca.action_plans.add_action_plan_modal.title")}
            showCloseButton
            customModalStyles={{ width: "90%", maxWidth: 1000 }}
            footer={footer}
        >
            <Formik
                innerRef={formRef}
                initialValues={initFormValues}
                validationSchema={addRCAActionRequestFormValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, values, handleChange, setFieldValue }) => {
                    return (
                        <Form>
                            <div className={classes.mainContainer}>
                                <div className={classes.leftContainer}>
                                    <div className={classes.leftContentWrapper}>
                                        <div className={classes.issueAndActionPlanNumberWrapper}>
                                            {issue && (
                                                <div className={classes.issueAndActionPlanNumberItem}>
                                                    <img src={IssueIcon} alt="Issue icon" />
                                                    {`${t("rca.iss")}-${issue?.issue_id}`}
                                                    {" / "}
                                                    <img src={ActionPlanIcon} alt="Action plan icon" />
                                                    {t("rca.action_plans.add_action_plan_modal.new_action_plan")}
                                                </div>
                                            )}
                                            {rca && (
                                                <div className={classes.issueAndActionPlanNumberItem}>
                                                    <img src={RCAIcon} alt="RCA icon" />
                                                    {`${t("rca.rca")}-${rca?.rca_id}`}
                                                    {" / "}
                                                    <img src={ActionPlanIcon} alt="Action plan icon" />
                                                    {t("rca.action_plans.add_action_plan_modal.new_action_plan")}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`${classes.form} ${classes.formLeft}`}>
                                            <Box className={mainClasses.contentFormBox}>
                                                <InputLabel id="outlined-actionPlanDescription-label">
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={mainClasses.contentFormLabel}
                                                    >
                                                        {issue
                                                            ? t(
                                                                  "rca.action_plans.add_action_plan_modal.parent_issue_description",
                                                              )
                                                            : t(
                                                                  "rca.action_plans.add_action_plan_modal.parent_rca_description",
                                                              )}
                                                    </Typography>
                                                </InputLabel>
                                                <TextField
                                                    disabled
                                                    fullWidth
                                                    className={`${mainClasses.contentFormMultitextField} ${mainClasses.contentNoBorderedTextField}`}
                                                    multiline
                                                    rows={3}
                                                    type="text"
                                                    variant="outlined"
                                                    value={issue ? issue.description : rca?.incident_description}
                                                />
                                            </Box>
                                            <Box className={mainClasses.contentFormBox}>
                                                <InputLabel id="outlined-actionPlanDescription-label">
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={mainClasses.contentFormLabel}
                                                    >
                                                        {t("rca.action_plans.add_action_plan_modal.action_plan_name")}
                                                    </Typography>
                                                </InputLabel>
                                                <TextField
                                                    disabled={isSaving}
                                                    aria-labelledby="outlined-actionPlanDescription-label"
                                                    fullWidth
                                                    id="outlined-actionPlanDescription-input"
                                                    className={mainClasses.contentFormTextField}
                                                    name="actionPlanName"
                                                    type="text"
                                                    variant="outlined"
                                                    value={values.actionPlanName}
                                                    onChange={handleChange}
                                                    error={touched.actionPlanName && Boolean(errors.actionPlanName)}
                                                    helperText={
                                                        touched.actionPlanName &&
                                                        errors.actionPlanName &&
                                                        t(errors.actionPlanName)
                                                    }
                                                />
                                            </Box>
                                            <Box className={mainClasses.contentFormBox}>
                                                <InputLabel id="outlined-actionPlanDescription-label">
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={mainClasses.contentFormLabel}
                                                    >
                                                        {t(
                                                            "rca.action_plans.add_action_plan_modal.action_plan_description",
                                                        )}
                                                    </Typography>
                                                </InputLabel>
                                                <TextField
                                                    disabled={isSaving}
                                                    aria-labelledby="outlined-actionPlanDescription-label"
                                                    fullWidth
                                                    id="outlined-actionPlanDescription-input"
                                                    className={mainClasses.contentFormTextField}
                                                    name="actionPlanDescription"
                                                    type="text"
                                                    variant="outlined"
                                                    rows={3}
                                                    multiline
                                                    value={values.actionPlanDescription}
                                                    onChange={handleChange}
                                                    error={
                                                        touched.actionPlanDescription &&
                                                        Boolean(errors.actionPlanDescription)
                                                    }
                                                    helperText={
                                                        touched.actionPlanDescription &&
                                                        errors.actionPlanDescription &&
                                                        t(errors.actionPlanDescription)
                                                    }
                                                />
                                            </Box>
                                        </div>
                                    </div>
                                </div>

                                <div className={classes.rightContainer}>
                                    <div className={classes.form}>
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-keyPerformanceIndicatorsIds-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.action_plans.add_action_plan_modal.due_date")}
                                                </Typography>
                                            </InputLabel>

                                            <DatePicker
                                                className={mainClasses.contentFormDatePicker}
                                                value={dayjs(values.dueDate)}
                                                disablePast
                                                format={getDateFormatByLanguage()}
                                                onChange={(date) => {
                                                    setFieldValue("dueDate", date);
                                                }}
                                            />
                                        </Box>
                                        <Divider />
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-assignedToId-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.action_plans.add_action_plan_modal.owner.title")}
                                                </Typography>
                                            </InputLabel>
                                            <RCAUsersDropdown
                                                fullWidth
                                                millId={rca?.mill_id || issue?.mill_id}
                                                multiple={false}
                                                value={values.assignedToId}
                                                onChange={(value) => {
                                                    setFieldValue("assignedToId", value);
                                                }}
                                                placeholder={t(
                                                    "rca.action_plans.add_action_plan_modal.owner.select_owner",
                                                )}
                                            />

                                            {!!errors.assignedToId && !!touched.assignedToId && (
                                                <FormHelperText className={mainClasses.contentFormHelperText}>
                                                    {t(errors.assignedToId)}
                                                </FormHelperText>
                                            )}
                                        </Box>
                                        <Divider />
                                        <RCAPriorityDropdown
                                            errors={errors}
                                            handleChange={(value) => {
                                                setFieldValue("priorityId", value);
                                            }}
                                            touched={touched}
                                            value={values.priorityId}
                                        />
                                        <Divider />
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-meetingTypeId-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.rca_library.rca_detail.meeting_type")}
                                                </Typography>
                                            </InputLabel>
                                            <RCAMeetingTypesDropdown
                                                fullWidth
                                                multiple={false}
                                                value={values.meetingTypeId}
                                                onChange={(value) => {
                                                    setFieldValue("meetingTypeId", value);
                                                }}
                                            />
                                        </Box>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </MainModal>
    );
};

export default RCAAddActionPlanModal;
